import { useRef, useState, useEffect, useCallback } from 'react'
import s from './styles.module.scss'
import { head } from 'helpers'
import { AdvancedSelect, Alert, InfiniteScroll, Layouts, Loader, NoData, Search, Table } from 'components'
import * as api from 'api'
import { Link } from 'react-router-dom'

export default function Main() {
  const [users, setUsers] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  // pagination removed
  const [searchedUsers, setSearchedUsers] = useState([])
  const [state, setState] = useState('')
  const [states, setStates] = useState([])
  const [reRender, setReRender] = useState(false)
  const [city, setCity] = useState('')
  const [cities, setCities] = useState([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const processing = useRef(false)
  const tableRef = useRef(null)
  const perPage = 1000 // load a large batch; no pagination

  useEffect(() => {
    head({ title: 'Users | BCN' })
  }, [])

  // initial load once (independent of search)
  useEffect(() => {
    getUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getStates = useCallback(async stateSearch => {
    if (processing.current) return
    processing.current = true

    const fetchStateSuggestions = await api.publicApi.locations.fetchAllStates({ limit: 10, state: stateSearch })

    if (fetchStateSuggestions.code === 200) {
      setStates(fetchStateSuggestions.payload.states)
    } else {
      // Alert.error(fetchStateSuggestions.message)
    }
    processing.current = false
  }, [])

  useEffect(() => {
    getStates('')
  }, [getStates])

  const getCities = useCallback(
    async citySearch => {
      if (processing.current) return
      processing.current = true
      let fkStateId = ''

      const stateSelected = await api.publicApi.locations.fetchAllStates({ limit: 10, state })

      if (stateSelected.code === 200) {
        fkStateId = stateSelected.payload.states[0]._id
      } else return

      const fetchCitySuggestions = await api.publicApi.locations.fetchAllCitiesViaState({
        limit: 10,
        city: citySearch,
        fkStateId
      })

      if (fetchCitySuggestions.code === 200) {
        setCities(fetchCitySuggestions.payload.cities)
      } else {
        // Alert.error(fetchCitySuggestions.message)
      }

      setReRender(false)
      processing.current = false
    },
    [state]
  )

  const getUsers = useCallback(
    async () => {
      if (processing.current) return
      processing.current = true

      setLoading(true)

      const fetchAllUsers = await api.admin.user.fetchAll({
        searchQuery,
        limit: perPage,
        pageNo: 1,
        state,
        city
      })

      if (fetchAllUsers.code === 200) {
        setUsers(fetchAllUsers.payload.Users)
        // console.log for debugging
        try { console.log('[DEBUG] Users fetched:', fetchAllUsers.payload.Users?.length) } catch (e) {}
      } else {
        Alert.error(fetchAllUsers.message)
      }
      processing.current = false
      setLoading(false)
    },
    [city, state, searchQuery]
  )

  const searchHandler = useCallback(
    async () => {
      if (processing.current) return
      processing.current = true

      setLoading(true)

      const fetchAllSearchedUsers = await api.admin.user.fetchAll({
        searchQuery,
        limit: perPage,
        pageNo: 1,
        state,
        city
      })

      if (fetchAllSearchedUsers.code === 200) {
        setSearchedUsers(fetchAllSearchedUsers.payload.Users)
      } else {
        Alert.error(fetchAllSearchedUsers.message)
      }
      processing.current = false
      setLoading(false)
    },
    [city, state, searchQuery]
  )

  useEffect(() => {
    if (state) getCities('')
  }, [state, getCities])

  // initial and reactive loads without pagination
  useEffect(() => {
    if (!searchQuery) {
      getUsers()
    }
  }, [getUsers, searchQuery])

  useEffect(() => {
    if (searchQuery) {
      searchHandler()
    }
  }, [searchHandler, searchQuery])

  return (
    <div className={s.main}>
      <div className={s.users}>
        <Layouts.Classic title='Users'>
          <div className={s.headerBottom}>
            <div className={s.headerFilters}>
              <Search
                placeholder='Search users by name/phone/email'
                searchHandler={search => {
                  setSearchQuery(search)
                  setSearchedUsers([])
                }}
                isAdmin
              />
              <AdvancedSelect
                defaultField={state ? state : 'Search state'}
                iconLeft='location_on'
                fieldName='state'
                list={states}
                changeHandler={getStates}
                listFieldHandler={field => {
                  setCity('')
                  setCities([])
                  setReRender(true)
                  setState(field.state)
                  setUsers([])
                  setSearchedUsers([])
                }}
              />
              {!!state && !reRender && (
                <AdvancedSelect
                  defaultField={city ? city : 'Select City'}
                  iconLeft='location_on'
                  fieldName='city'
                  list={cities}
                  changeHandler={getCities}
                  listFieldHandler={field => {
                    setUsers([])
                    setSearchedUsers([])
                    setCity(field.city)
                  }}
                />
              )}
            </div>
            <div className={s.headerActions}>
              <button
                type='button'
                className={s.exportBtn}
                onClick={() => tableRef.current?.exportToExcel()}
                disabled={exporting || loading || (!users.length && !searchedUsers.length)}
              >
                <span className='material-icons-outlined'>download</span>
                {exporting ? 'Exporting...' : 'Export'}
              </button>
              <Link className={s.addUser} to={'./addUser'}>
                <span className='material-icons-outlined'>add_circle_outline</span> Add User
              </Link>
            </div>
          </div>
        </Layouts.Classic>
        <div className={s.content} style={{ justifyContent: !users.length && 'center' }}>
          {!!searchQuery && <div className={s.searchResults}>"Search Results for {searchQuery}"</div>}
          {!loading && !users.length && !searchQuery && <NoData />}
          {!loading && !searchedUsers.length && searchQuery && <NoData />}

          {!searchQuery && !!users.length && (
            <div className={s.userData}>
              <Table.Users ref={tableRef} userData={users} onExportingChange={setExporting} />
            </div>
          )}
          {!!searchQuery && !!searchedUsers.length && (
            <div className={s.userData}>
              <Table.Users ref={tableRef} userData={searchedUsers} onExportingChange={setExporting} />
            </div>
          )}

          {!!loading && <Loader color='var(--c-primary)' colorText='var(--c-primary)' />}
        </div>
      </div>
    </div>
  )
}
